import { css } from '@linaria/core'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  EnhancedConfigTypeId,
  EnhancedLanguageId,
  EnhancedMarketId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes.ts'
import { tmsApiService } from '#api/tmsApiService'

const {
  useGetApiUserdataGetmarketsQuery,
  useGetApiProductdataMarketByMarketIdLanguagesQuery,
  useGetApiProductdataGetviewheadersByMarketIdQuery,
  useGetApiProductdataGetvehicletypeidsQuery,
  useGetApiProductdataGetconfigurationtypeidsQuery,
} = tmsApiService

const loadingClass = css`
  opacity: 0.6;
  animation: pulse 1s infinite;
  padding-right: 4px;

  /* pulse animation */
  @keyframes pulse {
    0% {
      opacity: 0.3;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.3;
    }
  }
`

export const MarketLabel = ({ marketId }: { marketId: EnhancedMarketId | undefined }) => {
  const { data, error, isLoading } = useGetApiUserdataGetmarketsQuery()

  const { t } = useTranslation('shared')

  const label = useMemo(() => {
    if (data) {
      const market = data.markets?.find((m) => m.marketId === marketId)
      return market?.marketName
    }
    return undefined
  }, [data, marketId])

  if (marketId === undefined) {
    return <span>{t('ecm.labels.market-name.undefined', 'Undefined Market')}</span>
  }

  if (error) {
    return <span>{t('ecm.labels.market-name.failed', 'Failed to load markets')}</span>
  }

  if (isLoading) {
    return <span className={loadingClass}>{t('ecm.labels.market-name.loading', 'Loading...')}</span>
  }

  return <span>{label ?? t('ecm.labels.market-name.unnamed', 'Unnamed Market')}</span>
}

export const LanguageLabel = ({ languageId, marketId }: { languageId?: EnhancedLanguageId; marketId: EnhancedMarketId | undefined }) => {
  const { data, error, isLoading } = useGetApiProductdataMarketByMarketIdLanguagesQuery(
    {
      marketId: marketId as EnhancedMarketId,
    },
    { skip: !marketId },
  )

  const { t } = useTranslation('shared')

  const label = useMemo(() => {
    if (data) {
      const language = data.listOfLanguage?.find((l) => l.languageID === languageId)
      return language?.languageName
    }
    return undefined
  }, [data, languageId])

  if (languageId === undefined) {
    return <span>{t('ecm.labels.language-name.undefined', 'Undefined Language')}</span>
  }

  if (error) {
    return <span>{t('ecm.labels.language-name.failed', 'Failed to load languages')}</span>
  }

  if (isLoading) {
    return <span className={loadingClass}>{t('ecm.labels.language-name.loading', 'Loading...')}</span>
  }

  return <span>{label ?? t('ecm.labels.language-name.unnamed', 'Unnamed Language')}</span>
}

export const ViewLabel = ({ viewId, marketId }: { viewId?: EnhancedViewId; marketId: EnhancedMarketId | undefined }) => {
  const { data, error, isLoading } = useGetApiProductdataGetviewheadersByMarketIdQuery(
    { marketId: marketId as EnhancedMarketId },
    { skip: !marketId },
  )

  const { t } = useTranslation('shared')

  const label = useMemo(() => {
    if (data) {
      const view = data.listOfViewHeader?.find((v) => v.viewID === viewId)
      return view?.viewName
    }
    return undefined
  }, [data, viewId])

  if (viewId === undefined) {
    return <span>{t('ecm.labels.view-name.undefined', 'Undefined View')}</span>
  }

  if (error) {
    return <span>{t('ecm.labels.view-name.failed', 'Failed to load views')}</span>
  }

  if (isLoading) {
    return <span className={loadingClass}>{t('ecm.labels.view-name.loading', 'Loading...')}</span>
  }

  return <span>{label ?? t('ecm.labels.view-name.unnamed', 'Unnamed View')}</span>
}

export const VehicleTypeLabel = ({ vehicleTypeId }: { vehicleTypeId?: EnhancedVehicleTypeId }) => {
  const { data, error, isLoading } = useGetApiProductdataGetvehicletypeidsQuery()

  const { t } = useTranslation('shared')

  const label = useMemo(() => {
    if (data) {
      const vehicle = data.listOfVehicleTypes?.find((v) => v.vehicleTypeID === vehicleTypeId)
      return vehicle?.vehicleTypeName
    }
    return undefined
  }, [data, vehicleTypeId])

  if (vehicleTypeId === undefined) {
    return <span>{t('ecm.labels.vehicle-type.undefined', 'Undefined Vehicle Type')}</span>
  }

  if (error) {
    return <span>{t('ecm.labels.vehicle-type.failed', 'Failed to load vehicle types')}</span>
  }

  if (isLoading) {
    return <span className={loadingClass}>{t('ecm.labels.vehicle-type.loading', 'Loading...')}</span>
  }

  return <span>{label ?? t('ecm.labels.vehicle-type.unnamed', 'Unnamed Vehicle Type')}</span>
}

export const ConfigurationTypeLabel = ({ configurationTypeId }: { configurationTypeId?: EnhancedConfigTypeId }) => {
  const { data, error, isLoading } = useGetApiProductdataGetconfigurationtypeidsQuery()

  const { t } = useTranslation('shared')

  const label = useMemo(() => {
    if (data) {
      const config = data.listOfConfigTypes?.find((c) => c.configTypeID === configurationTypeId)
      return config?.configTypeName
    }
    return undefined
  }, [data, configurationTypeId])

  if (configurationTypeId === undefined || configurationTypeId === null) {
    return <span>{t('ecm.labels.configuration-type.undefined', 'Undefined Configuration Type')}</span>
  }

  if (error) {
    return <span>{t('ecm.labels.configuration-type.failed', 'Failed to load configuration types')}</span>
  }

  if (isLoading) {
    return <span className={loadingClass}>{t('ecm.labels.configuration-type.loading', 'Loading...')}</span>
  }

  return <span>{label ?? t('ecm.labels.configuration-type.unnamed', 'Unnamed Configuration Type')}</span>
}
